import { AlertTriangle } from 'lucide-react'

import type { DataReviewDecisionInput, DataReviewItem } from '@/services/dataReview'
import { EvidenceDetails, PriorDecision, type OpenEvidence } from './ReparseEvidence'
import { show } from './reparseFormat'
import { useDatapointOptions } from './useDatapointOptions'
import { kindMeta } from './dataReviewFormat'
import { DecisionBar } from './DecisionBar'

type Answer = Omit<DataReviewDecisionInput, 'itemId' | 'note' | 'overrideHumanDecision'>

/**
 * The engine now reads this package differently from what the record holds.
 *
 * What the record holds and what the engine reads now, side by side, each with
 * the document and place it came from. A re-parse REPLACES a value somebody may
 * have chosen on purpose, so nothing here applies on its own and nothing here
 * answers more than this one field: keep what is stored, take the new reading,
 * type a third value, or — when the engine can no longer support the stored
 * value at all — say it is not available.
 *
 * A value a PERSON decided is shown with that fact attached and the primary
 * action is keeping it. Any other answer meets the server's refusal first, and
 * the panel then asks whether to withdraw their decision or overrule it.
 */
export function ReparseItemPanel({
  item, busy, value, onValue, reason, onReason, onDecide, onOpenSource,
}: {
  item: DataReviewItem
  busy: boolean
  value: string
  onValue: (value: string) => void
  reason: string
  onReason: (reason: string) => void
  onDecide: (answer: Answer) => void
  onOpenSource?: OpenEvidence
}) {
  const proposal = item.detail?.proposal
  const unsupported = item.kind === 'unsupported'
  const field = item.field ?? ''
  const optionsFor = useDatapointOptions()
  const meta = kindMeta(item.kind)
  const offers = (verb: DataReviewItem['actions'][number]) => item.actions.includes(verb)

  return (
    <div className="space-y-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-stone-100">{item.label}</span>
          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[8px] font-medium uppercase tracking-wide ${meta.cls}`}>
            {meta.label}
          </span>
          {item.humanDecided && (
            <span className="inline-flex items-center gap-1 rounded border border-amber-400/30 bg-amber-400/10 px-1 py-px text-[9px] uppercase tracking-wide text-amber-200">
              <AlertTriangle className="h-2.5 w-2.5" /> decided by {item.decidedBy ?? 'a person'}
            </span>
          )}
        </div>
        <p className="mt-1 text-[10.5px] leading-relaxed text-stone-400">{item.question}</p>
        {unsupported && (
          <p className="mt-1 text-[10px] leading-relaxed text-stone-500">
            The engine re-read this package and no longer finds the value — a name nobody in it
            identifies, or a figure an earlier engine misread. The stored value is kept unless
            you correct or clear it.
          </p>
        )}
        {proposal?.priorDecision ? <PriorDecision decision={proposal.priorDecision} /> : null}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] px-2.5 py-2">
          <div className="text-[9px] font-medium uppercase tracking-wider text-stone-500">Stored value</div>
          <div className="mt-1 break-words font-mono text-[12px] font-semibold text-stone-100">
            {show(item.value, field)}
          </div>
          <EvidenceDetails
            evidence={proposal?.currentEvidence}
            fallbackSource={proposal?.currentSource}
            sourceRole="Stored source"
            onOpenSource={onOpenSource}
          />
        </div>

        {unsupported ? (
          <div className="rounded-lg border border-amber-400/15 bg-amber-400/[0.03] px-2.5 py-2">
            <div className="text-[9px] font-medium uppercase tracking-wider text-amber-200/70">
              Current engine finding
            </div>
            <div className="mt-1 text-[11px] text-amber-100/80">No supported value</div>
            <EvidenceDetails
              evidence={proposal?.incomingEvidence}
              fallbackSource={proposal?.incomingSource}
              sourceRole="Proposal source"
              onOpenSource={onOpenSource}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-sky-400/30 bg-sky-400/[0.06] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-medium uppercase tracking-wider text-stone-500">
                The engine’s new reading
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => onDecide({ verb: 'correct', value: proposal?.incoming })}
                className={`${item.humanDecided ? 'btn-secondary' : 'btn-primary'} px-2.5 py-1 text-[10.5px] disabled:opacity-40`}
              >
                Use this reading
              </button>
            </div>
            <div className="mt-1 break-words font-mono text-[12px] font-semibold text-sky-200">
              {show(proposal?.incoming, field)}
            </div>
            <EvidenceDetails
              evidence={proposal?.incomingEvidence}
              fallbackSource={proposal?.incomingSource}
              sourceRole="Proposal source"
              onOpenSource={onOpenSource}
            />
          </div>
        )}
      </div>

      <div className="sticky bottom-0 -mx-4 border-t border-stone-800 bg-stone-950/95 px-4 pb-1 pt-2.5 backdrop-blur">
        <DecisionBar
          busy={busy}
          confirm={offers('confirm') ? {
            label: item.humanDecided ? 'Keep their answer' : 'Keep the stored value',
            title: 'Leave the record as it is. The engine’s reading is set aside and not asked again — '
              + 'unless the engine reads something different or the stored value changes.',
            primary: item.humanDecided,
            onClick: () => onDecide({ verb: 'confirm' }),
          } : undefined}
          correct={offers('correct') ? {
            value,
            onValue,
            placeholder: 'Enter the correct value',
            options: optionsFor(field),
            onUse: (typed) => onDecide({ verb: 'correct', value: typed }),
          } : undefined}
          unavailable={offers('unavailable') ? {
            reason,
            onReason,
            onUse: (why) => onDecide({ verb: 'unavailable', reason: why }),
          } : undefined}
        />
        <p className="mt-1 text-[9px] text-stone-600">
          Each answer applies to this field only and is recorded against your name.
        </p>
      </div>
    </div>
  )
}
